/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

/* The building blocks of the patient help pages: the landing page and the pages an emailed exam link shows
 * when it can't open the exam in the browser it landed in. */

import React, { ReactElement, ReactNode } from "react"
import { Link } from "react-router-dom"

const supportPhone = { display: "1-888-633-2422", href: "tel:+18886332422" }
const supportEmail = "support@medaica.com"

const buttonClasses = {
  primary:
    "flex flex-col items-center justify-center rounded-md bg-primary-500 hover:bg-primary-600 text-white " +
    "font-semibold px-5 py-5 text-center transition-colors",
  secondary:
    "flex flex-col items-center justify-center rounded-md bg-white border border-primary text-primary " +
    "hover:bg-primary-100 font-semibold px-5 py-5 text-center transition-colors",
}

/** A large call-to-action button. Renders a router Link for in-app paths (`to`) or an anchor for external URLs
 *  (`href`), so in-app targets resolve against the current environment's origin. An external URL opens in a new
 *  window unless `newWindow` is false. */
const ActionButton = ({
  to,
  href,
  newWindow = true,
  variant,
  title,
  subtitle,
}: {
  to?: string
  href?: string
  newWindow?: boolean
  variant: keyof typeof buttonClasses
  title: string
  subtitle: string
}): ReactElement => {
  const content = (
    <>
      <span className="text-lg">{title}</span>
      <span className="text-sm font-normal opacity-90 mt-1">{subtitle}</span>
    </>
  )
  if (to) {
    return (
      <Link to={to} className={buttonClasses[variant]}>
        {content}
      </Link>
    )
  }
  return newWindow ? (
    <a href={href} className={buttonClasses[variant]} target="_blank" rel="noopener noreferrer">
      {content}
    </a>
  ) : (
    <a href={href} className={buttonClasses[variant]}>
      {content}
    </a>
  )
}

/** A titled card. With `step`, the title is numbered and the content lines up under the title's text. */
const Card = ({
  title,
  lead,
  step,
  compact = false,
  children,
}: {
  title: string
  lead?: ReactNode
  step?: number
  compact?: boolean
  children: ReactNode
}): ReactElement => {
  const indent = step ? "ml-10" : "ml-5"
  return (
    <div className={`bg-white border border-gray-200 rounded-lg shadow-sm mb-6 ${compact ? "p-5" : "p-8"}`}>
      <h2 className="flex items-center gap-3 text-xl font-bold">
        {step ? (
          <span
            className="flex items-center justify-center w-7 h-7 rounded-full bg-secondary text-sm text-gray-900"
            aria-hidden="true"
          >
            {step}
          </span>
        ) : (
          <span className="w-2 h-5 rounded-full bg-secondary" aria-hidden="true" />
        )}
        {title}
      </h2>
      {lead ? <p className={`text-gray-600 mt-1 mb-6 ${indent}`}>{lead}</p> : <div className="mb-5" />}
      {children}
    </div>
  )
}

/** The support phone number and email, on the brand gradient. */
const HelpBand = ({ heading, lead }: { heading: string; lead?: string }): ReactElement => {
  return (
    <div className="rounded-lg bg-gradient-to-br from-primary-500 to-primary-700 text-white text-center px-8 py-12">
      <h2 className="text-2xl font-bold">{heading}</h2>
      {lead && <p className="mt-2 opacity-90">{lead}</p>}
      <a href={supportPhone.href} className="block text-3xl font-bold mt-6 hover:opacity-80">
        {supportPhone.display}
      </a>
      <a href={`mailto:${supportEmail}`} className="block text-xl mt-1 opacity-90 hover:opacity-80">
        {supportEmail}
      </a>
    </div>
  )
}

export { ActionButton, Card, HelpBand }
